import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { authClient, useSession } from "~/lib/auth-client";
import { StudentVerificationCard } from "~/components/StudentVerificationCard";
import { trackEvent } from "~/lib/posthog-client";
import * as m from "~/paraglide/messages";
import {
  softwareAppSchema,
  breadcrumbSchema,
  faqPageSchema,
  schemaGraph,
} from "~/lib/seo/schema";
import { JsonLd } from "~/components/seo/JsonLd";
import { formatCurrency } from "~/lib/formatting";

/* ---- Credit Pack configuration (D-19, D-20) ---- */

const PACK_CONFIGS = [
  { type: "starter" as const, name: "Starter", credits: 25, priceEur: 4.99, perCredit: 0.20 },
  { type: "boost" as const, name: "Boost", credits: 60, priceEur: 9.99, perCredit: 0.17 },
  { type: "power" as const, name: "Power", credits: 150, priceEur: 19.99, perCredit: 0.13 },
] as const;

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Preise & Plaene | Alchema" },
      {
        name: "description",
        content:
          "Alchema Preise: kostenlos, Student ab EUR 14,99/Monat, Pro ab EUR 34,99/Monat. KI-gestuetzte Bewerbungshilfe mit menschlicher Pruefung vor dem Absenden.",
      },
      { property: "og:title", content: "Preise & Plaene | Alchema" },
      {
        property: "og:description",
        content:
          "Alchema Preise: Kostenloser Plan, Student ab EUR 14,99/Monat, Pro ab EUR 34,99/Monat. Bis zu 40 % sparen mit Jahresabo.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alchema.eu/pricing" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Preise & Plaene | Alchema" },
      {
        name: "twitter:description",
        content:
          "KI-gestuetzte Bewerbungsunterstuetzung ab EUR 0/Monat. Student und Pro Plaene.",
      },
    ],
  }),
  component: PricingPage,
});

interface TierFeature {
  text: () => string;
  included: boolean;
}

interface TierConfig {
  name: () => string;
  tier: "free" | "student" | "pro"; // lifetime removed (D-16, D-17)
  monthlyPrice: number;
  annualMonthlyPrice: number;
  annualTotalPrice: number;
  features: TierFeature[];
  badge?: () => string;
  highlighted?: boolean;
  ctaLabel: () => string;
  desc: () => string;
  savingsPercent: number;
}

function getTiers(): TierConfig[] {
  return [
    {
      name: () => m.pricing_plan_free_name(),
      tier: "free",
      monthlyPrice: 0,
      annualMonthlyPrice: 0,
      annualTotalPrice: 0,
      savingsPercent: 0,
      features: [
        { text: () => m.pricing_feature_ai_actions_5(), included: true },
        { text: () => m.pricing_feature_1_resume(), included: true },
        { text: () => m.pricing_feature_10_job_tracking(), included: true },
        { text: () => m.pricing_feature_unlimited_job_search(), included: true },
        { text: () => m.pricing_feature_ats_score(), included: true },
        { text: () => m.pricing_feature_jd_analysis(), included: true },
        { text: () => m.pricing_feature_basic_salary(), included: true },
        { text: () => m.pricing_feature_interview_prep(), included: false },
      ],
      ctaLabel: () => m.pricing_plan_free_cta(),
      desc: () => m.pricing_plan_free_desc(),
    },
    {
      name: () => m.pricing_plan_student_name(),
      tier: "student",
      monthlyPrice: 14.99,
      annualMonthlyPrice: 8.99,
      annualTotalPrice: 107.88,
      savingsPercent: 40,
      features: [
        { text: () => m.pricing_feature_ai_actions_75(), included: true },
        { text: () => m.pricing_feature_unlimited_resumes(), included: true },
        { text: () => m.pricing_feature_unlimited_job_tracking(), included: true },
        { text: () => m.pricing_feature_full_salary(), included: true },
        { text: () => m.pricing_feature_interview_prep(), included: true },
        { text: () => m.pricing_feature_company_briefs(), included: true },
        { text: () => m.pricing_feature_pdf_docx_export(), included: true },
        { text: () => m.pricing_feature_email_support(), included: true },
      ],
      badge: () => m.pricing_plan_student_badge(),
      highlighted: true,
      ctaLabel: () => m.pricing_plan_student_cta(),
      desc: () => m.pricing_plan_student_desc(),
    },
    {
      name: () => m.pricing_plan_pro_name(),
      tier: "pro",
      monthlyPrice: 34.99,
      annualMonthlyPrice: 24.99,
      annualTotalPrice: 299.88,
      savingsPercent: 29,
      features: [
        { text: () => m.pricing_feature_ai_actions_300(), included: true },
        { text: () => m.pricing_feature_everything_student(), included: true },
        { text: () => m.pricing_feature_full_analytics(), included: true },
        { text: () => m.pricing_feature_deep_research(), included: true },
        { text: () => m.pricing_feature_salary_negotiation(), included: true },
        { text: () => m.pricing_feature_priority_support(), included: true },
      ],
      ctaLabel: () => m.pricing_plan_pro_cta(),
      desc: () => m.pricing_plan_pro_desc(),
    },
  ];
}

/* ---- Feature comparison data ---- */

interface ComparisonRow {
  feature: () => string;
  free: string;
  student: string;
  pro: string;
}

function getComparisonRows(): ComparisonRow[] {
  return [
    { feature: () => m.pricing_compare_resumes(), free: "1", student: m.pricing_compare_unlimited(), pro: m.pricing_compare_unlimited() },
    { feature: () => m.pricing_compare_job_tracking(), free: "10", student: m.pricing_compare_unlimited(), pro: m.pricing_compare_unlimited() },
    { feature: () => m.pricing_compare_ai_actions(), free: "5 / mo", student: "75 / mo", pro: "300 / mo" },
    { feature: () => m.pricing_compare_cover_letters(), free: m.pricing_compare_yes(), student: m.pricing_compare_yes(), pro: m.pricing_compare_yes() },
    { feature: () => m.pricing_compare_ats_score(), free: m.pricing_compare_yes(), student: m.pricing_compare_yes(), pro: m.pricing_compare_yes() },
    { feature: () => m.pricing_compare_interview_prep(), free: "--", student: m.pricing_compare_yes(), pro: m.pricing_compare_yes() },
    { feature: () => m.pricing_compare_salary_data(), free: m.pricing_compare_basic(), student: m.pricing_compare_full(), pro: m.pricing_compare_full_negotiation() },
    { feature: () => m.pricing_compare_company_briefs(), free: "--", student: m.pricing_compare_yes(), pro: m.pricing_compare_yes() },
    { feature: () => m.pricing_compare_export(), free: "PDF", student: "PDF + DOCX", pro: "PDF + DOCX" },
    { feature: () => m.pricing_compare_analytics(), free: "--", student: "--", pro: m.pricing_compare_yes() },
    { feature: () => m.pricing_compare_support(), free: m.pricing_compare_community(), student: m.pricing_compare_email(), pro: m.pricing_compare_priority() },
  ];
}

/* ---- Check / Dash icons ---- */

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className ?? "n8n-check"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function DashIcon() {
  return (
    <svg
      className="n8n-check"
      style={{ stroke: "var(--text-muted)" }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
    </svg>
  );
}

/* ---- Animated Price ---- */

function useAnimatedNumber(target: number, duration = 400): string {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number>(0);
  const startRef = useRef({ value: target, time: 0 });
  const prefersReduced = useRef(false);

  useEffect(() => {
    prefersReduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (prefersReduced.current) {
      setDisplay(target);
      return;
    }
    const from = display;
    if (from === target) return;
    startRef.current = { value: from, time: performance.now() };

    const animate = (now: number) => {
      const elapsed = now - startRef.current.time;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startRef.current.value + (target - startRef.current.value) * eased;
      setDisplay(current);
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return display.toFixed(2);
}

function AnimatedPrice({ price, prefix }: { price: number; prefix: string }) {
  const animated = useAnimatedNumber(price);
  return <>{prefix.replace("{price}", animated)}</>;
}

function isAuthenticationError(error: unknown): boolean {
  const record = error && typeof error === "object" ? error as Record<string, unknown> : {};
  const data = record.data && typeof record.data === "object" ? record.data as Record<string, unknown> : {};
  const response = record.response && typeof record.response === "object" ? record.response as Record<string, unknown> : {};
  const code = typeof record.code === "string"
    ? record.code
    : typeof data.code === "string"
      ? data.code
      : undefined;
  const status = typeof record.status === "number"
    ? record.status
    : typeof record.statusCode === "number"
      ? record.statusCode
      : typeof response.status === "number"
        ? response.status
        : undefined;
  const message = error instanceof Error
    ? error.message
    : typeof record.message === "string"
      ? record.message
      : "";

  return code === "UNAUTHORIZED" || status === 401 || /not authenticated|unauthorized|\b401\b/i.test(message);
}

/* ---- Main Component ---- */

function PricingPage() {
  const navigate = useNavigate();
  // Default to annual billing (PRICE-04)
  const [annual, setAnnual] = useState(true);
  const [checkoutTarget, setCheckoutTarget] = useState<string | null>(null);
  const checkoutMutation = useMutation({
    ...orpc.billing.checkout.mutationOptions(),
    onError: (error) => {
      if (isAuthenticationError(error)) {
        redirectToSignup();
      }
    },
  });
  const { data: session } = useSession();
  const tierQuery = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    enabled: !!session?.user,
    retry: 0,
  });
  const verifyQuery = useQuery({
    ...orpc.verify.status.queryOptions(),
    enabled: !!session?.user,
    retry: 0,
  });
  const currentTier = tierQuery.data?.tier;
  const isVerified = (verifyQuery.data as { verified: boolean } | undefined)?.verified;
  const queriesReady = !session?.user || (tierQuery.isFetched && verifyQuery.isFetched);
  const showStudentVerification =
    queriesReady &&
    !!session?.user &&
    !isVerified;
  const tiers = getTiers();
  const comparisonRows = getComparisonRows();

  const pricingFaqs = [
    { question: m.pricing_faq_q1(), answer: m.pricing_faq_a1() },
    { question: m.pricing_faq_q2(), answer: m.pricing_faq_a2() },
    { question: m.pricing_faq_q3(), answer: m.pricing_faq_a3() },
    { question: m.pricing_faq_q4(), answer: m.pricing_faq_a4() },
    { question: m.pricing_faq_q5(), answer: m.pricing_faq_a5() },
    { question: m.pricing_faq_q6(), answer: m.pricing_faq_a6() },
  ];

  async function hasCheckoutSession(): Promise<boolean> {
    if (session?.user) return true;

    try {
      const freshSession = await authClient.getSession({
        fetchOptions: {
          cache: "no-store",
          credentials: "include",
        },
      });
      return !!freshSession.data?.user;
    } catch {
      return false;
    }
  }

  function redirectToSignup() {
    void navigate({ to: "/auth/signup" });
  }

  async function handleCheckout(tier: "student" | "pro") {
    if (checkoutMutation.isPending) return;
    trackEvent("upgrade_clicked", { source: "pricing_page", current_tier: currentTier ?? "free", target_tier: tier });

    if (!(await hasCheckoutSession())) {
      redirectToSignup();
      return;
    }

    setCheckoutTarget(tier);

    // Read Affonso affiliate referral cookie for attribution
    const affiliateRef = typeof document !== "undefined"
      ? document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("affonso_ref="))?.split("=")[1]
      : undefined;
    checkoutMutation.mutate(
      { tier, billing: annual ? "annual" : "monthly", ...(affiliateRef ? { affiliateRef } : {}) },
      { onSuccess: (data) => { window.location.href = data.url; } },
    );
  }

  /* ---- Credit Pack purchase (D-19, D-22) ---- */

  const [widerrufsrechtConsent, setWiderrufsrechtConsent] = useState(false);
  const [widerrufsrechtError, setWiderrufsrechtError] = useState(false);
  const widerrufsrechtCheckboxRef = useRef<HTMLInputElement>(null);

  const packCheckoutMutation = useMutation({
    ...orpc.billing.purchaseCreditPack.mutationOptions(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      if (isAuthenticationError(error)) {
        redirectToSignup();
      }
    },
  });

  async function handlePackPurchase(packType: "starter" | "boost" | "power") {
    if (packCheckoutMutation.isPending) return;
    if (!(await hasCheckoutSession())) {
      redirectToSignup();
      return;
    }
    if (!widerrufsrechtConsent) {
      // Make the consent gate visible + actionable so the click is never a
      // silent no-op (KB-780). Surface the error, then bring the consent
      // control into view and move focus to it.
      setWiderrufsrechtError(true);
      const checkbox = widerrufsrechtCheckboxRef.current;
      if (checkbox) {
        checkbox.scrollIntoView({ behavior: "smooth", block: "center" });
        // Defer focus so it lands after the smooth scroll begins.
        requestAnimationFrame(() => checkbox.focus({ preventScroll: true }));
      }
      return;
    }
    setWiderrufsrechtError(false);
    setCheckoutTarget(packType);
    trackEvent("cta_clicked", { cta: "buy_pack", location: "pricing_page" });
    packCheckoutMutation.mutate({ packType, widerrufsrechtConsent: true as const });
  }

  // Handle ?billing=success
  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success") {
      setShowSuccess(true);
      window.history.replaceState({}, "", window.location.pathname);
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const pricingJsonLd = schemaGraph(
    softwareAppSchema({ includeOffers: true }),
    faqPageSchema(pricingFaqs),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Preise", url: "/pricing" },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={pricingJsonLd} />
      <section className="mx-auto max-w-6xl px-6 pb-32 pt-24">
        {/* Success toast */}
        {showSuccess && (
          <div
            className="mx-auto mb-8 max-w-lg rounded-[13px] border p-4 text-center text-sm"
            style={{
              borderColor: "var(--success)",
              backgroundColor: "var(--success-subtle)",
              color: "var(--success)",
            }}
          >
            {m.pricing_checkout_success()}
          </div>
        )}

        {/* Header */}
        <div className="pricing-header-glow text-center">
          <h1
            className="animate-fade-up text-4xl font-bold tracking-tight sm:text-5xl"
            style={{ color: "var(--text)" }}
          >
            {m.pricing_title()}
          </h1>
          <p
            className="animate-fade-up stagger-1 mx-auto mt-3 max-w-md text-sm leading-relaxed sm:text-base"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.pricing_subtitle()}
          </p>

          {/* Billing toggle — part of header group */}
          <div className="animate-fade-up stagger-2 mt-8 flex items-center justify-center gap-3">
            <span
              className="text-sm font-medium"
              style={{ color: annual ? "var(--text-muted)" : "var(--text)" }}
            >
              {m.common_monthly()}
            </span>
            <button
              onClick={() => setAnnual(!annual)}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
              style={{ backgroundColor: annual ? "var(--accent)" : "var(--border)" }}
              role="switch"
              aria-checked={annual}
              aria-label={m.pricing_toggle_annual_label()}
            >
              <span
                className="inline-block h-4 w-4 rounded-full bg-white transition-transform duration-300"
                style={{ transform: annual ? "translateX(22px)" : "translateX(4px)" }}
              />
            </button>
            <span
              className="text-sm font-medium"
              style={{ color: annual ? "var(--text)" : "var(--text-muted)" }}
            >
              {m.common_annual()}
            </span>
            {annual && (
              <span
                className="pricing-savings-enter ml-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{ backgroundColor: "var(--success-subtle)", color: "var(--success)" }}
              >
                {m.pricing_save_up_to({ percent: "40" })}
              </span>
            )}
          </div>

          {/* Annual preselect note */}
          {annual && (
            <p
              className="animate-fade-up mt-2 text-xs"
              style={{ color: "var(--text-muted)" }}
            >
              {m.pricing_annual_preselect_note()}
            </p>
          )}
        </div>

        {/* Student verification */}
        {showStudentVerification && <StudentVerificationCard />}

        {/* Tier cards */}
        <div className="animate-fade-up stagger-3 mt-14 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {tiers.map((t) => {
            const isFree = t.tier === "free";
            const displayPrice = annual ? t.annualMonthlyPrice : t.monthlyPrice;
            const showStrikethrough = annual && !isFree;
            const isCurrentPlan = currentTier === t.tier || (!currentTier && isFree);
            const currentPlanDisabled = isCurrentPlan;

            const cardClasses = [
              "n8n-pricing-card",
              t.highlighted ? "n8n-pricing-featured pricing-glow-border" : "",
              t.tier === "pro" && !isCurrentPlan ? "pricing-pro-glow" : "",
            ].filter(Boolean).join(" ");

            return (
              <div
                key={t.tier}
                className={cardClasses}
                style={isCurrentPlan ? {
                  border: "1.5px solid var(--accent)",
                } : undefined}
              >
                {isCurrentPlan && (
                  <span
                    className="n8n-pricing-badge"
                    style={{
                      backgroundColor: "var(--accent)",
                      color: "#fff",
                    }}
                  >
                    {m.pricing_current_plan()}
                  </span>
                )}
                {!isCurrentPlan && t.badge && (
                  <span className="n8n-pricing-badge">{t.badge()}</span>
                )}

                <div className="n8n-pricing-header">
                  <span className="n8n-pricing-tier">{t.name()}</span>
                  <div className="n8n-pricing-price">
                    {showStrikethrough && (
                      <span style={{ fontSize: "0.875rem", textDecoration: "line-through", color: "var(--text-muted)", marginRight: "0.375rem" }}>
                        {m.pricing_eur_price({ price: t.monthlyPrice.toFixed(2) })}
                      </span>
                    )}
                    <span className="n8n-pricing-amount">
                      {isFree ? m.pricing_eur_price({ price: "0" }) : <AnimatedPrice price={displayPrice} prefix={m.pricing_eur_price({ price: "{price}" })} />}
                    </span>
                    {!isFree && <span className="n8n-pricing-period">{m.pricing_per_month()}</span>}
                  </div>

                  {/* Savings badge — always rendered to prevent CLS, visibility toggled */}
                  {!isFree && t.savingsPercent > 0 && (
                    <span
                      className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${annual ? "pricing-savings-enter" : ""}`}
                      style={{
                        backgroundColor: "var(--success-subtle)",
                        color: "var(--success)",
                        opacity: annual ? 1 : 0,
                        transition: "opacity 200ms ease",
                      }}
                      aria-hidden={!annual}
                    >
                      {m.pricing_save_percent({ percent: String(t.savingsPercent) })}
                    </span>
                  )}

                  {!isFree && (
                    <p
                      style={{
                        marginTop: "0.375rem",
                        fontSize: "0.75rem",
                        color: "var(--text-secondary)",
                        opacity: annual ? 1 : 0,
                        transition: "opacity 200ms ease",
                      }}
                      aria-hidden={!annual}
                    >
                      {m.pricing_billed_annually({ amount: t.annualTotalPrice.toFixed(2) })}
                    </p>
                  )}
                  <p className="n8n-pricing-desc">{t.desc()}</p>
                </div>

                <ul className="n8n-pricing-features">
                  {t.features.map((f) => (
                    <li key={f.text()} style={{ opacity: f.included ? 1 : 0.4 }}>
                      {f.included ? <CheckIcon /> : <DashIcon />}
                      {f.text()}
                    </li>
                  ))}
                </ul>

                {currentPlanDisabled ? (
                  <span
                    className="n8n-pricing-cta"
                    style={{
                      opacity: 0.5,
                      cursor: "default",
                      pointerEvents: "none",
                    }}
                    aria-disabled="true"
                  >
                    {m.pricing_current_plan()}
                  </span>
                ) : isFree ? (
                  <Link
                    to="/auth/signup"
                    className="n8n-pricing-cta"
                  >
                    {t.ctaLabel()}
                  </Link>
                ) : (
                  <button
                    onClick={() => { void handleCheckout(t.tier as "student" | "pro"); }}
                    disabled={checkoutMutation.isPending}
                    className={`n8n-pricing-cta ${t.highlighted ? "n8n-pricing-cta-accent" : ""}`}
                  >
                    {checkoutMutation.isPending && checkoutTarget === t.tier ? m.common_redirecting() : t.ctaLabel()}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {checkoutMutation.isError && (
          <p
            role="alert"
            className="mt-4 text-center text-sm"
            style={{ color: "var(--error)" }}
          >
            {(checkoutMutation.error as Error)?.message ?? m.pricing_checkout_error()}
          </p>
        )}

        <p
          className="mx-auto mt-6 max-w-md text-center text-xs leading-relaxed"
          style={{ color: "var(--text-muted)" }}
        >
          {m.pricing_explainer()}
        </p>

        {/* ---- Credit Packs Section (D-19, D-20, D-22) ---- */}
        <div
          className="animate-fade-up mt-24 border-t pt-16"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          <h2
            className="text-center text-lg font-semibold tracking-tight"
            style={{ color: "var(--text)" }}
          >
            {m.pricing_credit_packs_title()}
          </h2>
          <p
            className="mx-auto mt-2 max-w-sm text-center text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.pricing_credit_packs_subtitle()}
          </p>

          {/* Widerrufsrecht disclosure + consent (BGB S356(5)) */}
          <div className="mx-auto mt-6 max-w-lg space-y-3">
            <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
              {m.widerrufsrecht_disclosure()}
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                ref={widerrufsrechtCheckboxRef}
                type="checkbox"
                checked={widerrufsrechtConsent}
                aria-invalid={widerrufsrechtError}
                aria-describedby={widerrufsrechtError ? "widerrufsrecht-error" : undefined}
                onChange={(e) => {
                  setWiderrufsrechtConsent(e.target.checked);
                  if (e.target.checked) setWiderrufsrechtError(false);
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border accent-[var(--accent)]"
                style={{ borderColor: "var(--border)" }}
              />
              <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {m.widerrufsrecht_checkbox()}
              </span>
            </label>
            {widerrufsrechtError && (
              <p
                id="widerrufsrecht-error"
                role="alert"
                className="text-xs font-medium"
                style={{ color: "var(--error)" }}
              >
                {m.widerrufsrecht_required()}
              </p>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {PACK_CONFIGS.map((pack, i) => (
              <div
                key={pack.type}
                className={`n8n-pricing-card--pack animate-fade-up stagger-${i + 1}`}
              >
                <span className="n8n-pricing-tier">{pack.name}</span>
                <div className="n8n-pricing-amount" style={{ fontSize: "2rem" }}>
                  {formatCurrency(pack.priceEur)}
                </div>
                <span className="n8n-pricing-period">
                  {m.pricing_pack_onetime()}
                </span>
                <div className="mt-3 space-y-1">
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {m.pricing_pack_credits({ count: String(pack.credits) })}
                  </p>
                  <p className="text-[0.8125rem]" style={{ color: "var(--text-muted)" }}>
                    {m.pricing_pack_per_credit({ price: formatCurrency(pack.perCredit) })}
                  </p>
                </div>
                <button
                  onClick={() => { void handlePackPurchase(pack.type); }}
                  disabled={packCheckoutMutation.isPending || !widerrufsrechtConsent}
                  aria-disabled={packCheckoutMutation.isPending || !widerrufsrechtConsent}
                  className="n8n-pricing-cta n8n-pricing-cta-accent mt-5"
                  aria-label={m.pricing_pack_get_credits({ count: String(pack.credits) })}
                  aria-describedby={widerrufsrechtError ? "widerrufsrecht-error" : undefined}
                >
                  {packCheckoutMutation.isPending && checkoutTarget === pack.type
                    ? m.common_redirecting()
                    : m.pricing_pack_get_credits({ count: String(pack.credits) })}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Pack checkout error */}
        {packCheckoutMutation.isError && (
          <p
            role="alert"
            className="mt-4 text-center text-sm"
            style={{ color: "var(--error)" }}
          >
            {(packCheckoutMutation.error as Error)?.message ?? m.pricing_checkout_error()}
          </p>
        )}

        {/* ---- Feature Comparison Table ---- */}
        <div className="animate-fade-up mt-24">
          <h2
            className="text-center text-2xl font-bold tracking-tight"
            style={{ color: "var(--text)" }}
          >
            {m.pricing_compare_title()}
          </h2>
          <p
            className="mx-auto mt-3 max-w-md text-center text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.pricing_compare_subtitle()}
          </p>

          <div className="pricing-table-glass mt-10 overflow-x-auto">
            <table
              className="w-full text-sm"
              style={{ minWidth: "600px" }}
              role="table"
              aria-label={m.pricing_compare_title()}
            >
              <thead>
                <tr
                  className="border-b"
                  style={{ borderColor: "var(--border-subtle)" }}
                >
                  <th
                    className="pb-3 text-left font-medium"
                    style={{ color: "var(--text-muted)", width: "40%" }}
                  >
                    {m.pricing_compare_feature_header()}
                  </th>
                  <th
                    className="pb-3 text-center font-medium"
                    style={{ color: "var(--text-muted)", width: "20%" }}
                  >
                    {m.pricing_plan_free_name()}
                  </th>
                  <th
                    className="pb-3 text-center font-medium"
                    style={{ color: "var(--accent-text)", width: "20%" }}
                  >
                    {m.pricing_plan_student_name()}
                  </th>
                  <th
                    className="pb-3 text-center font-medium"
                    style={{ color: "var(--text-muted)", width: "20%" }}
                  >
                    {m.pricing_plan_pro_name()}
                  </th>
                </tr>
              </thead>
              <tbody style={{ color: "var(--text-secondary)" }}>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={row.feature()}
                    className={i < comparisonRows.length - 1 ? "border-b" : ""}
                    style={{ borderColor: "var(--border-subtle)" }}
                  >
                    <td className="py-3 font-medium" style={{ color: "var(--text)" }}>
                      {row.feature()}
                    </td>
                    <ComparisonCell value={row.free} />
                    <ComparisonCell value={row.student} />
                    <ComparisonCell value={row.pro} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---- FAQ Section (GEO-09) ---- */}
        <section className="animate-fade-up mt-24" aria-labelledby="pricing-faq-heading">
          <div className="mx-auto max-w-2xl">
            <h2
              id="pricing-faq-heading"
              className="text-center text-2xl font-bold tracking-tight"
              style={{ color: "var(--text)" }}
            >
              {m.pricing_faq_title()}
            </h2>
            <div className="mt-10 space-y-0">
              {pricingFaqs.map((faq, i) => (
                <details
                  key={i}
                  className="group border-b py-4"
                  style={{ borderColor: "var(--border-subtle)" }}
                >
                  <summary
                    className="cursor-pointer list-none font-medium text-sm"
                    style={{ color: "var(--text)" }}
                  >
                    <span className="flex items-center justify-between">
                      <span>{faq.question}</span>
                      <svg
                        className="ml-2 h-4 w-4 shrink-0 transition-transform duration-200 group-open:rotate-180"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        style={{ color: "var(--text-muted)" }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-20 max-w-2xl rounded-[13px] border p-6 text-center" style={{ borderColor: "var(--border-subtle)" }} aria-label={m.consumer_declaration_type_label()}>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/widerruf" className="btn btn-secondary">
              {m.consumer_declaration_withdraw_first()}
            </Link>
            <Link to="/kuendigung" className="btn btn-secondary">
              {m.consumer_declaration_termination_first()}
            </Link>
          </div>
        </section>

        {/* Footer */}
        <p
          className="mt-10 text-center text-xs tracking-wide"
          style={{ color: "var(--text-muted)" }}
        >
          {m.pricing_footer()}
        </p>
      </section>
    </div>
  );
}

/* ---- Comparison table cell ---- */

function ComparisonCell({ value }: { value: string }) {
  const isDash = value === "--";
  return (
    <td
      className="py-3 text-center"
      style={isDash ? { color: "var(--text-muted)" } : {}}
    >
      {isDash ? (
        <span className="inline-block" aria-label="Not included">--</span>
      ) : (
        value
      )}
    </td>
  );
}
